import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DateRangePicker, {
  type DateRangePickerValue,
} from '@/components/basic/date-picker/date-range-picker';
import type {AgentDateRangeParams} from '../agent.type';
import {getAgentDateRangeLabel, toAgentDateKey} from './agent-data.store';

const triggerClassName =
  'relative flex h-12 min-w-0 items-center justify-between gap-1 rounded-lg border-0 bg-[rgba(255,255,255,0.72)] px-2 text-[0.75rem] font-normal leading-none text-black shadow-[inset_0_0_0_0.0625rem_rgba(255,255,255,0.45)] backdrop-blur-[0.125rem] [touch-action:manipulation] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-0.125rem] focus-visible:outline-[#FB6B0B]/40';

const getTodayDateRangeValue = (): DateRangePickerValue => {
  const today = new Date();
  return {
    startDate: today,
    endDate: today,
  };
};

export const useAgentDateRange = () => {
  const [dateRangeValue, setDateRangeValue] = useState<DateRangePickerValue>(
    getTodayDateRangeValue,
  );
  const range = useMemo<AgentDateRangeParams>(
    () => ({
      startDate: toAgentDateKey(dateRangeValue.startDate),
      endDate: toAgentDateKey(dateRangeValue.endDate),
    }),
    [dateRangeValue],
  );

  return {
    range,
    dateRangeValue,
    setDateRangeValue,
  };
};

export interface AgentDateRangeFilterProps {
  className?: string;
  range: AgentDateRangeParams;
  value: DateRangePickerValue;
  onChange: (value: DateRangePickerValue) => void;
}

const AgentDateRangeFilter = ({
  className = '',
  range,
  value,
  onChange,
}: AgentDateRangeFilterProps) => {
  const {t} = useTranslation();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label={t('datePicker.label.select')}
        onClick={() => setDatePickerOpen(true)}
        className={[triggerClassName, className].filter(Boolean).join(' ')}>
        <span className="min-w-0 truncate">
          {getAgentDateRangeLabel(range)}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="0.875rem"
          height="0.875rem"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
          className="shrink-0">
          <path
            d="M7.76517 9.12713C7.3664 9.58759 6.65209 9.58759 6.25331 9.12713L3.24606 5.65465C2.68519 5.00701 3.14524 4 4.00199 4L10.0165 4C10.8732 4 11.3333 5.00701 10.7724 5.65465L7.76517 9.12713Z"
            fill="black"
          />
        </svg>
      </button>
      <DateRangePicker
        open={datePickerOpen}
        setOpen={setDatePickerOpen}
        value={value}
        quickOptions={['lastWeek', 'lastMonth']}
        variant="referEarnWhite"
        onConfirm={onChange}
      />
    </>
  );
};

export default AgentDateRangeFilter;
